import { ReactNode } from 'react';

import { DocumentTypeWithVirtual, ValueOf } from 'support/types';

import {
  useCreditNoteSummary,
  useDebitNoteSummary,
  useInvoiceSummary,
  useServiceInvoiceSummary,
} from './hooks/useAccountingDocumentSummary';
import { useDispatchInstructionSummary } from './hooks/useDispatchInstructionSummary';
import { useOrderResponseSummary } from './hooks/useOrderResponseSummary';
import { useOrderSummary } from './hooks/useOrderSummary';
import { usePriceCatalogSummary } from './hooks/usePriceCatalogSummary';
import { useRemittanceAdviceSummary } from './hooks/useRemittanceAdviceSummary';
import { useSalesReportSummary } from './hooks/useSalesReportSummary';
import { useShipmentSummary } from './hooks/useShipmentSummary';
import { Summary } from './types';

export const DocumentSummary = ({
  documentType,
  renderSummary,
}: {
  documentType: ValueOf<DocumentTypeWithVirtual> | undefined;
  renderSummary: ({ summary }: { summary: Summary }) => ReactNode;
}) => {
  if (!documentType) {
    return null;
  }

  switch (documentType) {
    case DocumentTypeWithVirtual.order:
      return <OrderSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.orderResponse:
    case DocumentTypeWithVirtual.orderConfirmation:
    case DocumentTypeWithVirtual.partialOrderConfirmation:
    case DocumentTypeWithVirtual.orderRejection:
      return <OrderResponseSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.dispatchInstruction:
      return <DispatchInstructionSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.shippingNotice:
    case DocumentTypeWithVirtual.dispatchResponse:
      return <ShipmentSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.invoice:
      return <InvoiceSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.serviceInvoice:
      return <ServiceInvoiceSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.creditNote:
      return <CreditNoteSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.debitNote:
      return <DebitNoteSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.productCatalog:
      return <ProductCatalogSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.remittanceAdvice:
      return <RemittanceAdviceSummary renderSummary={renderSummary} />;
    case DocumentTypeWithVirtual.salesReport:
      return <SalesReportSummary renderSummary={renderSummary} />;
  }
  return null;
};

type SummaryRendererProps = {
  renderSummary: ({ summary }: { summary: Summary }) => ReactNode;
};
const OrderSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useOrderSummary();
  return renderSummary({ summary });
};

const OrderResponseSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useOrderResponseSummary();
  return renderSummary({ summary });
};

const ShipmentSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useShipmentSummary();
  return renderSummary({ summary });
};
const InvoiceSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useInvoiceSummary();
  return renderSummary({ summary });
};
const ServiceInvoiceSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useServiceInvoiceSummary();
  return renderSummary({ summary });
};

const CreditNoteSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useCreditNoteSummary();
  return renderSummary({ summary });
};
const DebitNoteSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useDebitNoteSummary();
  return renderSummary({ summary });
};

const ProductCatalogSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = usePriceCatalogSummary();
  return renderSummary({ summary });
};
const RemittanceAdviceSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useRemittanceAdviceSummary();
  return renderSummary({ summary });
};

const DispatchInstructionSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useDispatchInstructionSummary();
  return renderSummary({ summary });
};

const SalesReportSummary = ({ renderSummary }: SummaryRendererProps) => {
  const summary = useSalesReportSummary();
  return renderSummary({ summary });
};
